import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CommonStyles from "@components/CommonStyles";
import MyPackageCard from "./MyPackageCard";
import { ESpecialPackageStatus } from "@base/enums";
import useCheckLogin from "@base/hooks/useCheckLogin";
import { baseRoutes } from "@base/routes/baseRoutes";
import { ISpecialPackage } from "@base/modules/patientSpecialPackage/patientSpecialPackage.interface";
import patientSpecialPackageService from "@base/modules/patientSpecialPackage/patientSpecialPackageService";
import { responseUI } from "@base/themes/response";

/** How many packages the account call brings back; the home page then keeps the active ones. */
const ACCOUNT_PACKAGE_PAGE_SIZE = 50;

/**
 * The same slider as the leading doctors: 3 / 2 / 1 per view. It only loops, and only shows its dots,
 * when there are more packages than fit - a single package is not repeated to fill the row.
 */
const sliderSettings = (count: number) => {
  const fits = (perView: number) => ({ slidesToShow: perView, infinite: count > perView, dots: count > perView });

  return {
    ...fits(3),
    speed: 500,
    centerPadding: "0px",
    slidesToScroll: 1,
    nextArrow: <></>,
    prevArrow: <></>,
    responsive: [
      { breakpoint: 1600, settings: fits(3) },
      { breakpoint: 1200, settings: fits(2) },
      { breakpoint: 600, settings: fits(1) },
    ],
  };
};

export default function MyPackage() {
  //! state
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLogin } = useCheckLogin();

  const [packages, setPackages] = useState<ISpecialPackage[]>([]);

  //! function
  const getInProgressPackages = useCallback(async () => {
    try {
      // As on mobile: the whole account (`my-packages`, no status), narrowed to what is in progress
      // and ordered by `display_priority` here, so the most important package is never on a later page.
      const resp = await patientSpecialPackageService.findAll({ page_number: 0, page_size: ACCOUNT_PACKAGE_PAGE_SIZE });

      setPackages(
        (resp.data.content_page ?? [])
          .filter((specialPackage) => specialPackage.status === ESpecialPackageStatus.IN_PROGRESS)
          .sort((a, b) => Number(b.display_priority ?? 0) - Number(a.display_priority ?? 0)),
      );
    } catch (error) {}
  }, []);

  useEffect(() => {
    if (!isLogin) {
      return;
    }

    getInProgressPackages();
  }, [isLogin, getInProgressPackages]);

  //! render
  // A guest has nothing to show here, and neither has a patient with no package in progress - in
  // both cases the section disappears rather than leaving an empty block on the home page.
  if (!isLogin || !packages.length) {
    return null;
  }

  return (
    <div className="bg-common-white">
      <div className={`${responseUI.screen} pt-10 lg:pt-14 pb-10 lg:pb-20 ${responseUI.padding}`}>
        <div className="flex justify-between items-center mb-4 lg:mb-10">
          <div className="basis-2/3">
            <CommonStyles.TypographyUI use="title" className={`${responseUI.text.title}`} level={3}>
              {t("user.servicePackage.servicePackages")}
            </CommonStyles.TypographyUI>
            <CommonStyles.TypographyUI use="paragraph" className={`${responseUI.text.subTitle}`}>
              {t("home.subMyPackage")}
            </CommonStyles.TypographyUI>
          </div>

          <CommonStyles.ButtonUI onClick={() => navigate(baseRoutes.userPackageList)} ghost className="lg:h-12 lg:text-base font-medium">
            {t("home.viewAll")}
          </CommonStyles.ButtonUI>
        </div>

        {/* Slides stretch to the tallest card, so a one-line name does not leave a shorter card.
            Fewer packages than fit start at the left: slick centres a short track by default. */}
        <div className="[&_.slick-track]:flex [&_.slick-track]:!ml-0 [&_.slick-slide]:!h-auto [&_.slick-slide>div]:h-full">
          <CommonStyles.CarouselUI settings={sliderSettings(packages.length)}>
            {packages.map((specialPackage: ISpecialPackage, idx: number) => {
              return (
                <div key={idx} className="px-3 pt-1 pb-8 h-full">
                  <MyPackageCard specialPackage={specialPackage} />
                </div>
              );
            })}
          </CommonStyles.CarouselUI>
        </div>
      </div>
    </div>
  );
}
